import { TranslatePipe } from '@myre/ui-primitive-core';

import {
  Component,
  EventEmitter,
  forwardRef,
  input,
  Input,
  Output,
} from '@angular/core';
import {
  FormsModule,
  NG_VALUE_ACCESSOR,
  type ControlValueAccessor,
} from '@angular/forms';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';

export interface MyreRadioButtonOption<T> {
  label?: string;
  value?: T;
  readonly?: boolean;
  onChange?: (value: any) => void;
}

export const RADIO_GROUP_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => RadioGroupComponent),
  multi: true,
};

@Component({
  selector: 'myre-radio-group',
  templateUrl: './radio-group.component.html',
  styleUrls: ['./radio-group.component.scss'],
  providers: [RADIO_GROUP_VALUE_ACCESSOR],
  imports: [FormsModule, MatRadioButton, MatRadioGroup, TranslatePipe],
})
export class RadioGroupComponent<T = unknown> implements ControlValueAccessor {
  @Input()
  public id: string;

  @Input()
  public display: 'line' | 'column' = 'line';

  @Input()
  public justify: 'space-between' | 'start' | 'center' = 'space-between';

  public box = input<boolean>(false);

  @Input()
  public labelPosition: 'before' | 'after' = 'after';

  @Input()
  public options: MyreRadioButtonOption<T>[] = [];

  @Input()
  public required: boolean = false;

  @Input()
  public readonly: boolean = false;

  public disabled: boolean = false;

  public value: T;

  @Output()
  public change = new EventEmitter<T>();

  public writeValue(value: T): void {
    this.value = value;
  }

  public registerOnChange(fn: () => any): void {
    this._onModelChange = fn;
  }

  public registerOnTouched(fn: () => any): void {
    this._onTouched = fn;
  }

  public setDisabledState(value: boolean): void {
    this.disabled = value;
  }

  public onChange(value: T): void {
    this._onModelChange(value);
    this.change.emit(value);

    const selectedOption = this.options.find(o => o.value === value);

    if (selectedOption?.onChange) {
      selectedOption.onChange(value);
    }
  }

  public onBlur(): void {
    this._onTouched();
  }

  private _onModelChange: (value: T) => any = () => {};

  private _onTouched: () => any = () => {};
}
